import {
  Component,
  ChangeDetectionStrategy,
  viewChild,
  input,
  output,
  inject,
  signal
} from '@angular/core';

import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ViewportRuler } from '@angular/cdk/scrolling';

import { NgTemplateOutlet } from '@angular/common';
import { HorizontalScrollDirection } from '../../domain/direction';
import { PipesModule } from '../../pipes/pipes.module';
import { IconComponent } from '../icon/icon.component';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { ScrollableDirective } from './scrollable.directive';
import {
  SCROLL_EDGE_THRESHOLD,
  SCROLL_OPTIONS_STRATEGY
} from './scroll-options-strategy';
import { DefaultScrollOptionsStrategy } from './default-scroll-options-strategy';
import type {
  ScrollOptionsStrategy,
  HorizontalScrollPosition
} from './scroll-options-strategy';
import type {
  AfterViewInit,
  OnDestroy,
  TemplateRef,
  InputSignal,
  OutputEmitterRef,
  Signal,
  WritableSignal
} from '@angular/core';
import type { ExtendedScrollToOptions } from '@angular/cdk/scrolling';

/**
 * The Tabs Scroll Component
 *
 */
@Component({
  selector: 'td-wb-scrollable-container',
  templateUrl: 'scrollable-container.component.html',
  styleUrls: ['scrollable-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, ScrollableDirective, NgTemplateOutlet, PipesModule, IconButtonComponent]
})
export class ScrollableContainerComponent implements AfterViewInit, OnDestroy {

  private static readonly DEBOUNCE_DELAY_MS: number = 300;

  borderRadius: InputSignal<boolean> = input<boolean>(true);

  content: InputSignal<TemplateRef<unknown> | undefined> = input<TemplateRef<unknown>>();

  positions: InputSignal<ReadonlyArray<HorizontalScrollPosition> | undefined> = input<ReadonlyArray<HorizontalScrollPosition>>();

  scrollToOptions: InputSignal<ExtendedScrollToOptions | undefined> = input<ExtendedScrollToOptions>();

  elementScrolled: OutputEmitterRef<ExtendedScrollToOptions> = output<ExtendedScrollToOptions>();

  scrollable: Signal<ScrollableDirective> = viewChild.required(ScrollableDirective);

  showPrevButton: WritableSignal<boolean> = signal<boolean>(false);
  showNextButton: WritableSignal<boolean> = signal<boolean>(false);

  private readonly viewportRuler: ViewportRuler = inject(ViewportRuler);
  private readonly scrollOptionsProvider: ScrollOptionsStrategy;
  private readonly destroy: Subject<void> = new Subject<void>();

  constructor() {
    const scrollOptionsProvider = inject(SCROLL_OPTIONS_STRATEGY, { optional: true });
    this.scrollOptionsProvider = scrollOptionsProvider === null ? new DefaultScrollOptionsStrategy() : scrollOptionsProvider;
  }

  /**
   * This function is called when the view has been fully initialized and is used to subscribe to tab list changes,
   */
  ngAfterViewInit(): void {
    this.scrollHandler();
    this.scrollable().refreshScrollSize();

    const scrollToOptions = this.scrollToOptions();
    if (scrollToOptions !== undefined) {
      this.scrollable().scrollTo(scrollToOptions);
    }

    this.scrollable().elementScrolled()
      .pipe(
        debounceTime(ScrollableContainerComponent.DEBOUNCE_DELAY_MS),
        takeUntil(this.destroy)
      )
      .subscribe((): void => {
        this.scrollHandler();
        this.scrollable().refreshScrollSize();
        this.elementScrolled.emit({ left: this.scrollable().measureScrollOffset(HorizontalScrollDirection.LEFT), behavior: 'smooth' });
      });

    this.viewportRuler.change(ScrollableContainerComponent.DEBOUNCE_DELAY_MS)
      .pipe(takeUntil(this.destroy))
      .subscribe((): void => {
        this.scrollHandler();
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  scrollToLeftEnd(): void {
    this.scrollable().scrollTo({ left: 0, behavior: 'smooth' });
  }

  scrollToRightEnd(): void {
    this.scrollable().scrollTo({ right: 0, behavior: 'smooth' });
  }

  scrollTo(scrollToOptions: ExtendedScrollToOptions): void {
    this.scrollable().scrollTo(scrollToOptions);
  }

  next(): void {
    const nextOptions = this.scrollOptionsProvider.scroll(HorizontalScrollDirection.RIGHT, this.scrollable(), this.positions() as Array<HorizontalScrollPosition>);
    if (nextOptions) {
      this.scrollable().scrollTo(nextOptions);
    }
  }

  previous(): void {
    const prevOptions = this.scrollOptionsProvider.scroll(HorizontalScrollDirection.LEFT, this.scrollable(), this.positions() as Array<HorizontalScrollPosition>);
    if (prevOptions) {
      this.scrollable().scrollTo(prevOptions);
    }
  }

  refreshView(): void {
    this.scrollHandler();
  }

  private scrollHandler(): void {
    const leftScrollOffset = this.scrollable().measureScrollOffset('left');
    const rightScrollOffset = this.scrollable().measureScrollOffset('right');
    this.showPrevButton.set(leftScrollOffset > SCROLL_EDGE_THRESHOLD);
    this.showNextButton.set(rightScrollOffset > SCROLL_EDGE_THRESHOLD);
  }
}
